import React from "react";
import { motion } from "framer-motion";
import { Plane, MapPin } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";

export default function Worldwide() {
  const { content } = useSiteContent();
  const w = content.worldwide;
  const cities = (w.cities || []).map((c) => ({
    ...c,
    slug: (c.city || "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "city",
  }));
  return (
    <section
      id="worldwide"
      data-testid="worldwide-section"
      className="band bg-[#FF2D9C] text-[#FAF5E8] py-20 lg:py-28"
    >
      <div className="max-w-[1500px] mx-auto px-5 lg:px-8 relative">
        {/* sticker row */}
        <div className="flex justify-end mb-6">
          <div className="wiggle inline-block" style={{ "--r": "5deg" }}>
            <div className="sticker bg-[#0A0A0A] text-[#FAF5E8] border-[#FAF5E8]" style={{ boxShadow: "3px 3px 0 #FAF5E8" }}>
              ✶ chapter 04
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#FAF5E8]/40 pb-5 font-mono text-[10px] uppercase tracking-[0.28em]">
          <span>④ Worldwide · Cyprus → 32 countries</span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FFD500] inline-block" />
            Live, every timezone
          </span>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="worldwide-title"
          className="font-display mt-10"
          style={{
            fontSize: "clamp(2.75rem, 9.5vw, 8.5rem)",
            lineHeight: 0.82,
            letterSpacing: "-0.025em",
          }}
        >
          Based in <span className="font-hand text-[#FFD500]" style={{letterSpacing:0}}>Cyprus.</span><br />
          Working <span className="text-outline-cream">worldwide.</span>
        </motion.h2>

        <div className="grid grid-cols-12 gap-8 mt-10">
          <div className="col-span-12 lg:col-span-7">
            <p
              data-testid="worldwide-body"
              className="font-mono text-base leading-relaxed text-[#FAF5E8]/90 max-w-2xl"
            >
              {w.body}
            </p>

            <div className="mt-7 grid grid-cols-2 gap-3 max-w-md font-mono text-[10px] uppercase tracking-[0.22em]">
              <div className="border-2 border-[#FAF5E8] rounded-lg p-3">
                <div className="text-[#FFD500]">Registered office</div>
                <div className="font-display text-2xl mt-1 normal-case tracking-normal">{w.spec_office}</div>
              </div>
              <div className="border-2 border-[#FAF5E8] rounded-lg p-3">
                <div className="text-[#FFD500]">Active markets</div>
                <div className="font-display text-2xl mt-1 normal-case tracking-normal">{w.spec_markets}</div>
              </div>
              <div className="border-2 border-[#FAF5E8] rounded-lg p-3">
                <div className="text-[#FFD500]">Coverage</div>
                <div className="font-display text-2xl mt-1 normal-case tracking-normal">{w.spec_coverage}</div>
              </div>
              <div className="border-2 border-[#FAF5E8] rounded-lg p-3">
                <div className="text-[#FFD500]">Travel</div>
                <div className="font-display text-2xl mt-1 normal-case tracking-normal inline-flex items-center gap-2">
                  <Plane className="w-5 h-5" /> {w.spec_travel}
                </div>
              </div>
            </div>
          </div>

          {/* Big stamp/star */}
          <div className="col-span-12 lg:col-span-5 flex items-start justify-center lg:justify-end relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
              whileInView={{ opacity: 1, scale: 1, rotate: -8 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="border-[3px] border-[#FAF5E8] rounded-full p-8 w-[280px] h-[280px] flex items-center justify-center text-center bg-[#FF2D9C]"
              style={{ boxShadow: "8px 8px 0 #0A0A0A" }}
              data-testid="worldwide-stamp"
            >
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em]">From</div>
                <div className="font-display text-5xl text-[#FFD500] mt-1">Cyprus</div>
                <div className="font-display text-2xl mt-2">to the world</div>
                <div className="font-hand text-2xl mt-2 text-[#0A0A0A]">↘ {w.stamp_year}</div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Cities */}
        <div className="mt-14 border-t-2 border-[#FAF5E8]/40 pt-6" data-testid="cities-grid">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FAF5E8]/80 mb-4">
            ✶ Roster & affiliate cities
          </div>
          <div className="flex flex-wrap gap-3">
            {cities.map((c, i) => (
              <motion.div
                key={c.slug}
                initial={{ opacity: 0, y: 12, rotate: 0 }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  rotate: i % 2 === 0 ? -2 : 2,
                }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                whileHover={{ rotate: 0, scale: 1.05 }}
                data-testid={`city-${c.slug}`}
                className={`px-4 py-3 rounded-full border-2 font-mono text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 ${
                  c.hot
                    ? "bg-[#0A0A0A] text-[#FFD500] border-[#FFD500]"
                    : "bg-[#FAF5E8] text-[#0A0A0A] border-[#FAF5E8]"
                }`}
                style={{ boxShadow: c.hot ? "3px 3px 0 #FAF5E8" : "3px 3px 0 #0A0A0A" }}
              >
                {c.hot && <MapPin className="w-3 h-3" />}
                <span className="font-display text-lg normal-case tracking-normal">
                  {c.city}
                </span>
                <span className="opacity-80">· {c.country}</span>
                {c.hot && <span className="ml-1 text-[#FF2D9C] bg-[#FFD500] px-1.5 rounded">HQ</span>}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
